import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ImgWithTitle from "../../component/workspaceContainer/ImgWithTitle";
import {
  LoginBackButton,
  PrimaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import "./InstitutionSelector.css";
import Dropdown from "../../component/dropdown/Dropdown";
import { APIGetInstitutions } from "../../config/utilities/APIs/institutionsApi";
import { APIGetMasterAdminInstitutions } from "../../config/utilities/APIs/masterAdmin/masterAdminApi";
import Cookies from "js-cookie";
import { useAuth } from "../../config/utilities/useAuth";
import { useTimezone } from "../../config/utilities/useTimezone";

/** IANA zone label + offset, e.g. Asia/Kuala Lumpur · GMT+8 */
const formatTimezoneRegionAndGmt = (ianaTz) => {
  const region = ianaTz.replace(/_/g, " ");
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: ianaTz,
      timeZoneName: "shortOffset",
    }).formatToParts(now);
    let offset =
      parts.find((p) => p.type === "timeZoneName")?.value?.replace(/\s+/g, "") ||
      "";
    if (offset.startsWith("UTC")) {
      offset = `GMT${offset.slice(3)}`;
    }
    return offset ? { region, gmt: offset } : { region, gmt: null };
  } catch {
    return { region, gmt: null };
  }
};

const InstitutionSelector = () => {
  const navigate = useNavigate();
  const { setTimezone } = useTimezone();
  const [fetchedInstitutionData, setFetchedInstitutionData] = useState({});
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState({
    institution: "",
  });
  const selectedInstitutionId = Cookies.get("selectedInstitutionId");
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [consoleMode, setConsoleMode] = useState(null);

  const {
    userId,
    privilege,
    privilegeSelection,
    setPrivilege,
    setTripTypes,
    token,
    loading: authLoading,
  } = useAuth();
  const isSuperAdmin = useMemo(
    () =>
      privilege?.role === "SUPERADMIN" ||
      privilegeSelection?.some((p) => p.role === "SUPERADMIN"),
    [privilege?.role, privilegeSelection]
  );

  const showConsoleChoice =
    isSuperAdmin && consoleMode === null && !selectedInstitutionId;

  const formatTripTypeLabel = (t) => {
    if (typeof t === "string") return t;
    if (t && typeof t === "object") {
      return t.name || t.label || t.id || String(t);
    }
    return String(t);
  };

  const selectedPreview = useMemo(() => {
    if (!input.institution || !Array.isArray(fetchedInstitutionData)) {
      return null;
    }
    const inst = fetchedInstitutionData.find(
      (i) => i.institutionId === input.institution
    );
    if (!inst) return null;

    const privilegeMatch = privilegeSelection?.find(
      (p) => p.institutionId === input.institution
    );
    const tripTypesFromInst = inst.tripTypes;
    const tripTypesFromPrivilege = privilegeMatch?.tripTypes;
    const tripTypes =
      Array.isArray(tripTypesFromInst) && tripTypesFromInst.length > 0
        ? tripTypesFromInst
        : Array.isArray(tripTypesFromPrivilege)
          ? tripTypesFromPrivilege
          : [];

    const timezone = inst.timezone ?? "Asia/Kuala_Lumpur";
    const { region, gmt } = formatTimezoneRegionAndGmt(timezone);

    return {
      institutionName: inst.institutionName,
      institutionId: inst.institutionId,
      timezone,
      timezoneRegion: region,
      timezoneGmt: gmt,
      tripTypes,
    };
  }, [input.institution, fetchedInstitutionData, privilegeSelection]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleContinue = async () => {
    if (!Array.isArray(fetchedInstitutionData)) {
      navigate("/dashboard", { replace: true });
      return;
    }

    const selectedInstitution = fetchedInstitutionData.find(
      (inst) => inst.institutionId === input.institution,
    );

    if (selectedInstitution) {
      Cookies.set("institutionName", selectedInstitution.institutionName);
      Cookies.set("selectedInstitutionId", selectedInstitution.institutionId);
      const tz = selectedInstitution.timezone ?? "Asia/Kuala_Lumpur";
      setTimezone(tz);
    }

    if (isSuperAdmin) {
      const nextPrivilege = {
        institutionId: selectedInstitution.institutionId,
        institutionName: selectedInstitution.institutionName,
        role: "SUPERADMIN",
        modules: {
          mapOverview: true,
          dispatchBoard: true,
          routeManagement: true,
          administrators: true,
          familyManagement: true,
          campaignManagement: true,
          vehicles: true,
          drivers: true,
          scheduling: true,
        },
        tripTypes: selectedInstitution.tripTypes || [],
        timezone: selectedInstitution.timezone ?? "Asia/Kuala_Lumpur",
      };

      setPrivilege(nextPrivilege);
      setTripTypes(nextPrivilege.tripTypes);
    } else {
      const selectedPrivilege = privilegeSelection.find(
        (p) => p.institutionId === input.institution
      );

      if (selectedPrivilege) {
        if (selectedPrivilege.modules) {
          for (const key in selectedPrivilege.modules) {
            const val = selectedPrivilege.modules[key];
            selectedPrivilege.modules[key] =
              typeof val === "string" ? val.toUpperCase() === "TRUE" : !!val;
          }
        }

        setPrivilege(selectedPrivilege);
        setTripTypes(selectedPrivilege.tripTypes);
      }
    }

    navigate("/dashboard", { replace: true });
  };

  const handleSelectAdminConsole = () => {
    setConsoleMode("admin");
  };

  const handleSelectMasterAdminConsole = () => {
    navigate("/master-admin", { replace: true });
  };

  const handleBackToConsoleSelection = () => {
    Cookies.remove("selectedInstitutionId");
    Cookies.remove("institutionName");
    setInput({ institution: "" });
    setConsoleMode(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      Cookies.remove("name");
      Cookies.remove("institutionId");
      Cookies.remove("email");
      Cookies.remove("instCount");

      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error.message);
    }
  };

  const fetchInstitutions = useCallback(async () => {
    if (!userId || !token) return;

    setLoading(true);

    try {
      const response = isSuperAdmin
        ? await APIGetMasterAdminInstitutions(setLoading, token)
        : await APIGetInstitutions(setLoading, userId);

      if (response?.status === 200 && response.data?.data) {
        const data = response.data.data;
        const options = data.map((institution) => ({
          label: institution.institutionName,
          value: institution.institutionId,
        }));

        setFetchedInstitutionData(data);
        setDropdownOptions(options);
      }
    } catch (error) {
      console.error("Error fetching institutions:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, token, isSuperAdmin]);

  useEffect(() => {
    if (authLoading || !userId || !token || showConsoleChoice) return;
    fetchInstitutions();
  }, [authLoading, userId, token, showConsoleChoice, fetchInstitutions]);

  /** Pre-select workspace from cookie when opening from dashboard (e.g. Institution Workspace). */
  useEffect(() => {
    if (!Array.isArray(fetchedInstitutionData) || fetchedInstitutionData.length === 0) {
      return;
    }
    const id = Cookies.get("selectedInstitutionId");
    if (!id) return;
    if (!fetchedInstitutionData.some((i) => i.institutionId === id)) return;
    setInput((prev) => {
      if (prev.institution !== "") return prev;
      return { ...prev, institution: id };
    });
  }, [fetchedInstitutionData]);

  if (showConsoleChoice) {
    return (
      <div className="agency-selector-container">
        <div className="agency-selector-heading">
          <ImgWithTitle
            titleText="Choose your console"
            subText="Select how you would like to proceed. You can switch between consoles at any time from the institution workspace page."
            icon="agency"
          />
        </div>
        <div className="institution-selector-body">
          <div className="console-choice-list">
            <button
              type="button"
              className="console-choice-card"
              onClick={handleSelectAdminConsole}
            >
              <span className="console-choice-card-title">Admin Console</span>
              <span className="console-choice-card-description">
                Select an institution workspace and manage day-to-day operations
                such as routes, scheduling, and users.
              </span>
            </button>
            <button
              type="button"
              className="console-choice-card console-choice-card--master"
              onClick={handleSelectMasterAdminConsole}
            >
              <span className="console-choice-card-title">
                Master Admin Console
              </span>
              <span className="console-choice-card-description">
                Manage institution workspaces, platform configuration, and
                cross-institution settings.
              </span>
            </button>
          </div>
          <div className="institution-back-container">
            <LoginBackButton
              label="Back to log in"
              btnClickBack={handleLogout}
              backgroundColor="#FFFFFF"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agency-selector-container">
      <div className="agency-selector-heading">
        <ImgWithTitle
          titleText="Institution Workspace"
          subText="You have access to multiple institution workspaces. Please select a workspace below to continue."
          icon="agency"
        />
      </div>
      <div className="institution-selector-body">
        <div className="institution-selector-main">
          <div className="institution-dropdown-container">
            <Dropdown
              placeholder="Select an institution..."
              options={dropdownOptions}
              name="institution"
              onChangeFunction={handleInputChange}
              input={input}
            />
          </div>
          <div className="agency-selector-list-agency">
            Do not see your institution? Please contact your institution
            administrator for assistance.
          </div>
          <div className="institution-continue-container">
            <PrimaryButton
              label="Continue"
              onClickFunction={handleContinue}
              disable={input.institution === ""}
            />
          </div>
          <div className="institution-back-container">
            {!selectedInstitutionId ? (
              <LoginBackButton
                label={
                  isSuperAdmin ? "Back to console selection" : "Back to log in"
                }
                btnClickBack={
                  isSuperAdmin
                    ? () => setConsoleMode(null)
                    : handleLogout
                }
                backgroundColor="#FFFFFF"
              />
            ) : (
              <LoginBackButton
                label={
                  isSuperAdmin ? "Back to console selection" : "Back to dashboard"
                }
                btnClickBack={
                  isSuperAdmin
                    ? handleBackToConsoleSelection
                    : () => navigate("/dashboard", { replace: true })
                }
                backgroundColor="#FFFFFF"
              />
            )}
          </div>
        </div>
        {selectedPreview && (
          <div className="institution-preview-below">
            <div
              className="institution-preview-card"
              aria-live="polite"
              aria-label="Selected institution details"
            >
              <div className="institution-preview-header">
                <div className="institution-preview-title">
                  Workspace preview
                </div>
              </div>
              <div className="institution-preview-name">
                {selectedPreview.institutionName}
              </div>
              <div className="institution-preview-id">
                ID: {selectedPreview.institutionId}
              </div>
              <div className="institution-preview-timezone-trip-row">
                <div className="institution-preview-split-col">
                  <span className="institution-preview-label">Timezone</span>
                  <span className="institution-preview-value">
                    {selectedPreview.timezoneGmt
                      ? `${selectedPreview.timezoneRegion} · ${selectedPreview.timezoneGmt}`
                      : selectedPreview.timezoneRegion}
                  </span>
                </div>
                <div className="institution-preview-split-col institution-preview-trip-types-col">
                  <span className="institution-preview-label">Trip Types</span>
                  <div className="institution-preview-tags">
                    {selectedPreview.tripTypes.length > 0 ? (
                      selectedPreview.tripTypes.map((t, idx) => (
                        <span
                          key={`${formatTripTypeLabel(t)}-${idx}`}
                          className="institution-preview-tag"
                        >
                          {formatTripTypeLabel(t)}
                        </span>
                      ))
                    ) : (
                      <span className="institution-preview-empty">
                        No trip types listed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstitutionSelector;
